/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
//import { ArrayExtensions, ObjectExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import classnames from 'classnames';

import { IProductSpecificationViewProps } from './dlvry-product-specification';
import { AllergensIconGrid } from '../../components/allergens-icon-grid';
import { getLocale } from '../../shared-utils/attributes/locale';
import { useBrandSchemes } from '../dlvry-buybox/hooks/use-brand-schemes';
import { getBrandSchemeDescription } from '../../shared-utils/brand-schemes/brand-schemes';
import DangerousGoods from './components/dangerous-goods';

const ProductSpecificationView: React.FC<IProductSpecificationViewProps> = (props) => {
    const {
        ProductSpecification,
        ProductSpecificationTableProps,
        ProductSpecificationTableBodyProps,
        productSpecificationResult,
        curatedInformation,
        ingredients,
        footer
    } = props;

    const classNameBase = 'ms-product-specification';

    const [selectedTab, setSelectedTab] = React.useState(0);
    const attributesLocale = React.useMemo(() => getLocale(props.context), [props.context]);
    const [brandSchemes] = useBrandSchemes(props.context, props.data.productSpecificationData.result);

    if (!productSpecificationResult) {
        return null;
    }

    const pages: {
        heading: string;
        subHeader: JSX.Element | null;
        content: JSX.Element;
    }[] = [];

    productSpecificationResult.forEach((table: any, index: number) => {
        if (!table || table.tableData.length === 0) {
            return null;
        }

        const heading = table.heading.props.text;
        const subHeader = table.subHeader;
        let content = (
            <Node {...ProductSpecificationTableProps} data-model-version={table.modelVersion}>
                <Node {...ProductSpecificationTableBodyProps}>{table.tableData}</Node>
            </Node>
        );

        if (table.name === attributesLocale.groups.productInformation) {
            content = (
                <div className={`${classNameBase}__product-information`}>
                    <div className={`${classNameBase}__product-information__first`}>
                        <AllergensIconGrid
                            className='msc-buybox__allergens-icon-grid'
                            context={props.context}
                            attributes={props.data.productSpecificationData.result}
                            type={'contains'}
                        />
                        <AllergensIconGrid
                            className='msc-buybox__allergens-icon-grid'
                            context={props.context}
                            attributes={props.data.productSpecificationData.result}
                            type={'contains-traces'}
                        />
                        {ingredients}
                    </div>
                    <div className={`${classNameBase}__product-information__second`}>
                        {content}
                    </div>
                    <div className={`${classNameBase}__product-information__third`}>
                        <DangerousGoods
                            baseClassName={classNameBase}
                            context={props.context}
                            resources={props.resources}
                            attributes={props.data.productSpecificationData.result}
                        />
                    </div>
                </div>
            );
        }

        return pages.push({ heading, subHeader, content });
    });

    if (props.addionalMedia) {
        pages.push({
            heading: props.resources.additionalTabLabel,
            subHeader: null,
            content: props.addionalMedia as JSX.Element
        });
    }

    if (brandSchemes && brandSchemes.length > 0) {
        pages.push({
            heading: attributesLocale.specialBadges,
            subHeader: null,
            content: (
                <div className='ms-product-specification__brand-schemes'>
                    {brandSchemes.map(x => (
                        <div key={x.id} className='ms-product-specification__brand-scheme'>
                            <img
                                className='ms-product-specification__brand-scheme-image'
                                src={x.imageUrl}
                                alt={x.name}
                                title={x.name}
                            />
                            <div className='ms-product-specification__brand-scheme-text'>
                                <h3 className='ms-product-specification__brand-scheme-name'>
                                    {x.name}
                                </h3>
                                <div
                                    className='ms-product-specification__brand-scheme-description'
                                    dangerouslySetInnerHTML={{ __html: getBrandSchemeDescription(x.id) || '' }}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            )
        });
    }

    props.slots?.tabs?.forEach((tab, index: number) => {
        pages.push({
            heading: (tab as JSX.Element).props?.config?.title || `Tab ${index + 1}`,
            subHeader: null,
            content: tab as JSX.Element
        });
    });

    return (
        <Module {...ProductSpecification}>
            {curatedInformation}
            {props.slots.productSheet}
            <div className={`${classNameBase}__tabs-header`}>
                {pages.map((page, index) => {
                    return (
                        <button
                            key={index}
                            className={classnames(`${classNameBase}__tab`, { selected: selectedTab === index })}
                            onClick={() => setSelectedTab(index)}
                        >
                            {page.heading}
                        </button>
                    );
                })}
            </div>
            <div className={`${classNameBase}__grid`}>
                {pages.map((page, index) => {
                    return (
                        <div key={page.heading.toLowerCase()} className={classnames(`${classNameBase}__group`, {
                            collapsed: selectedTab !== index,
                            expanded: selectedTab === index
                        })}>
                            {page.subHeader}
                            {page.content}
                        </div>
                    );
                })}
            </div>
            {footer}
        </Module>
    );
};

export default ProductSpecificationView;
